import type { TileKind } from "../engine/types/primitives";

type MapThemeConfig = {
  id: string;
  name: string;
  plainColors: readonly string[];
  plainDarkColor: string;
  seaColors: readonly string[];
  seaRingColor: string;
  seaFoamColor: string;
  seaDarkColor: string;
  shoreSoilColor: string;
  shoreSoilDarkColor: string;
  roadSurfaceColor: string;
  roadStripeColor: string;
  reachableTileFill: string;
  reachableTileHatch: string;
  tileNameOverrides: Partial<Record<TileKind, string>>;
  showInCodex: boolean;
};

const DEFAULT_REACHABLE_TILE_FILL = "rgba(255, 255, 255, 0.20)";
const DEFAULT_REACHABLE_TILE_HATCH = "rgba(255, 255, 255, 0.35)";
const ARCTIC_REACHABLE_TILE_FILL = "rgba(255, 184, 0, 0.20)";
const ARCTIC_REACHABLE_TILE_HATCH = "rgba(255, 184, 0, 0.30)";

export const MAP_THEME_CONFIGS = [
  {
    id: "classic",
    name: "Classic",
    plainColors: ["#8fcf93", "#94d098", "#8acb8e", "#92ce95"],
    plainDarkColor: "#58b661",
    seaColors: ["#4a90d9"],
    seaRingColor: "#98c0eb",
    seaFoamColor: "#d9e8f7",
    seaDarkColor: "#3c7cc8",
    shoreSoilColor: "#fcc390",
    shoreSoilDarkColor: "#fc9b45",
    roadSurfaceColor: "#7a7a7a",
    roadStripeColor: "#b8b8b8",
    reachableTileFill: DEFAULT_REACHABLE_TILE_FILL,
    reachableTileHatch: DEFAULT_REACHABLE_TILE_HATCH,
    tileNameOverrides: {},
    showInCodex: false,
  },
  {
    id: "volcanic",
    name: "Volcanic",
    plainColors: ["#a39790", "#a99f98", "#9d918a", "#a8948c"],
    plainDarkColor: "#7a6e66",
    seaColors: ["#c9431b"],
    seaRingColor: "#f08060",
    seaFoamColor: "#ffc8b0",
    seaDarkColor: "#b03c18",
    shoreSoilColor: "#c4a088",
    shoreSoilDarkColor: "#a07050",
    roadSurfaceColor: "#4a4142",
    roadStripeColor: "#bd593c",
    reachableTileFill: DEFAULT_REACHABLE_TILE_FILL,
    reachableTileHatch: DEFAULT_REACHABLE_TILE_HATCH,
    tileNameOverrides: {
      plain: "Ash Plains",
      forest: "Dead Forest",
      sea: "Lava",
    },
    showInCodex: true,
  },
  {
    id: "tropical",
    name: "Tropical",
    plainColors: ["#e8d86a", "#e4d465", "#eadc72", "#e2d060"],
    plainDarkColor: "#c8bc50",
    seaColors: ["#3dd5c4"],
    seaRingColor: "#7ee8dc",
    seaFoamColor: "#b8f5ee",
    seaDarkColor: "#32c4b4",
    shoreSoilColor: "#e8c898",
    shoreSoilDarkColor: "#d4a868",
    roadSurfaceColor: "#c4b08a",
    roadStripeColor: "#dbc9a8",
    reachableTileFill: DEFAULT_REACHABLE_TILE_FILL,
    reachableTileHatch: DEFAULT_REACHABLE_TILE_HATCH,
    tileNameOverrides: {
      plain: "Sandy Plains",
      forest: "Palm Grove",
      sea: "Lagoon",
    },
    showInCodex: true,
  },
  {
    id: "alien",
    name: "Alien",
    plainColors: ["#b8a4d4", "#b29ecc", "#bcaad8", "#ae98c8"],
    plainDarkColor: "#9688b8",
    seaColors: ["#6aefa0"],
    seaRingColor: "#a8f7cc",
    seaFoamColor: "#d0ffe8",
    seaDarkColor: "#58dc90",
    shoreSoilColor: "#c8b898",
    shoreSoilDarkColor: "#a89870",
    roadSurfaceColor: "#6b7280",
    roadStripeColor: "#ffec00",
    reachableTileFill: DEFAULT_REACHABLE_TILE_FILL,
    reachableTileHatch: DEFAULT_REACHABLE_TILE_HATCH,
    tileNameOverrides: {
      plain: "Xeno Plains",
      forest: "Spore Forest",
      sea: "Acid Lake",
    },
    showInCodex: true,
  },
  {
    id: "arctic",
    name: "Arctic",
    plainColors: ["#e8ecf2", "#ebf0f5", "#e5e9ef", "#eef2f6"],
    plainDarkColor: "#c8d0d8",
    seaColors: ["#092d77"],
    seaRingColor: "#4d7fc4",
    seaFoamColor: "#e8f2fa",
    seaDarkColor: "#123f8f",
    shoreSoilColor: "#d8dce4",
    shoreSoilDarkColor: "#b0b8c4",
    roadSurfaceColor: "#6ea4c6",
    roadStripeColor: "#e8f2fa",
    reachableTileFill: ARCTIC_REACHABLE_TILE_FILL,
    reachableTileHatch: ARCTIC_REACHABLE_TILE_HATCH,
    tileNameOverrides: {
      plain: "Snowy Plains",
    },
    showInCodex: true,
  },
  {
    id: "autumn",
    name: "Autumn",
    plainColors: ["#c4ae58", "#cbb45e", "#bea852", "#c8b25a"],
    plainDarkColor: "#9a843c",
    seaColors: ["#3d6178"],
    seaRingColor: "#7a9bb0",
    seaFoamColor: "#cddce8",
    seaDarkColor: "#2c4a5e",
    shoreSoilColor: "#c4a078",
    shoreSoilDarkColor: "#9a7048",
    roadSurfaceColor: "#7a5a38",
    roadStripeColor: "#e0c078",
    reachableTileFill: DEFAULT_REACHABLE_TILE_FILL,
    reachableTileHatch: DEFAULT_REACHABLE_TILE_HATCH,
    tileNameOverrides: {
      plain: "Golden Plains",
    },
    showInCodex: true,
  },
  {
    id: "sakura",
    name: "Sakura",
    plainColors: ["#98c9b0", "#9ecdb5", "#92c5ab", "#9acbb2"],
    plainDarkColor: "#6aa888",
    seaColors: ["#6aa8d8"],
    seaRingColor: "#b0d4ee",
    seaFoamColor: "#e8f4fb",
    seaDarkColor: "#4a8cc4",
    shoreSoilColor: "#f0d4cc",
    shoreSoilDarkColor: "#d4a8a0",
    roadSurfaceColor: "#8a929c",
    roadStripeColor: "#e4e8ee",
    reachableTileFill: DEFAULT_REACHABLE_TILE_FILL,
    reachableTileHatch: DEFAULT_REACHABLE_TILE_HATCH,
    tileNameOverrides: {
      forest: "Cherry Grove",
    },
    showInCodex: true,
  },
  {
    id: "mesa",
    name: "Mesa",
    plainColors: ["#d4b07a", "#dab680", "#ceb074", "#d6b47c"],
    plainDarkColor: "#b89058",
    seaColors: ["#1a8fa0"],
    seaRingColor: "#5ec4d0",
    seaFoamColor: "#c8eef0",
    seaDarkColor: "#147888",
    shoreSoilColor: "#e8c8a0",
    shoreSoilDarkColor: "#c49468",
    roadSurfaceColor: "#8a5038",
    roadStripeColor: "#e0c8a0",
    reachableTileFill: DEFAULT_REACHABLE_TILE_FILL,
    reachableTileHatch: DEFAULT_REACHABLE_TILE_HATCH,
    tileNameOverrides: {
      plain: "Dust Plains",
      forest: "Cactus Grove",
      sea: "Oasis",
    },
    showInCodex: true,
  },
  {
    id: "swamp",
    name: "Swamp",
    plainColors: ["#7a8e4c", "#80944e", "#748a48", "#7c904a"],
    plainDarkColor: "#5a6c34",
    seaColors: ["#3a3828"],
    seaRingColor: "#4a4634",
    seaFoamColor: "#4a4838",
    seaDarkColor: "#2a281c",
    shoreSoilColor: "#4a3c2c",
    shoreSoilDarkColor: "#32281c",
    roadSurfaceColor: "#4c3d2e",
    roadStripeColor: "#9a8860",
    reachableTileFill: DEFAULT_REACHABLE_TILE_FILL,
    reachableTileHatch: DEFAULT_REACHABLE_TILE_HATCH,
    tileNameOverrides: {
      plain: "Marsh Plains",
      forest: "Cypress Grove",
      sea: "Bog",
    },
    showInCodex: true,
  },
] as const satisfies readonly MapThemeConfig[];

export type MapTheme = (typeof MAP_THEME_CONFIGS)[number]["id"];

export const MAP_THEMES = MAP_THEME_CONFIGS.map(({ id }) => id) as [MapTheme, ...MapTheme[]];
export const DEFAULT_MAP_THEME = MAP_THEMES[0];

export function normalizeMapTheme(theme: unknown): MapTheme | undefined {
  if (theme === undefined || theme === null) return undefined;
  if (typeof theme === "string" && (MAP_THEMES as readonly string[]).includes(theme)) {
    return theme as MapTheme;
  }
  return DEFAULT_MAP_THEME;
}

export function mapThemeConfig(theme?: MapTheme): MapThemeConfig {
  const config = MAP_THEME_CONFIGS.find(({ id }) => id === (theme ?? DEFAULT_MAP_THEME));
  if (!config) throw new Error(`Unknown map theme: ${String(theme)}`);
  return config;
}

export const CODEX_MAP_THEMES = MAP_THEME_CONFIGS.filter((theme) => theme.showInCodex);
